<template>
  <box :title="t('Reprocessos')">
    <template #actions>
      <Button
        :data-testid="testIdPrefix + '-request'"
        icon="pi pi-plus"
        :label="t('Solicitar reprocesso')"
        :disabled="!canRequestReprocesso"
        @click="openReprocessoModal"
      />
    </template>
    <div v-if="reprocessos.length === 0" class="reprocessos-empty">
      {{ t('Nenhum reprocesso solicitado. Clique em "Solicitar reprocesso" para adicionar.') }}
    </div>
    <div v-else class="reprocessos-list">
      <div
        v-for="reprocesso in reprocessos"
        :key="reprocesso.so_reprocesso_id"
        :data-testid="testIdPrefix + '-row-' + reprocesso.so_reprocesso_id"
        class="reprocesso-item"
      >
        <div class="reprocesso-header">
          <div class="step-name">{{ reprocesso.so_production_step_name }}</div>
          <Tag
            :value="reprocesso.so_reprocesso_status === 'pending' ? t('Pendente') : t('Concluído')"
            :severity="reprocesso.so_reprocesso_status === 'pending' ? 'warning' : 'success'"
          />
        </div>

        <div v-if="reprocesso.so_reprocesso_notes" class="reprocesso-notes">
          <strong>{{ t('Motivo') }}:</strong> {{ reprocesso.so_reprocesso_notes }}
        </div>

        <div class="photos-grid">
          <div class="photo-item">
            <label>{{ t('Foto Antes') }}</label>
            <img
              v-if="reprocesso.so_reprocesso_photo_before"
              :src="reprocesso.so_reprocesso_photo_before"
              :alt="t('Foto antes')"
              class="preview-image"
            />
            <div v-else class="placeholder-image">
              {{ t('Nenhuma foto') }}
            </div>
          </div>
          <div class="photo-item">
            <label>{{ t('Foto Depois') }}</label>
            <img
              v-if="reprocesso.so_reprocesso_photo_after"
              :src="reprocesso.so_reprocesso_photo_after"
              :alt="t('Foto depois')"
              class="preview-image"
            />
            <template v-else-if="mode === 'verify' && editable">
              <CameraCapture
                :data-testid="testIdPrefix + '-photo-after-' + reprocesso.so_reprocesso_id"
                @photo-captured="(file: File) => handlePhotoAfterCaptured(reprocesso, file)"
              />
              <small v-if="uploadingId === reprocesso.so_reprocesso_id" class="uploading-hint">
                {{ t('Enviando foto...') }}
              </small>
            </template>
            <div v-else class="placeholder-image pending">
              {{ t('Aguardando finalização') }}
            </div>
          </div>
        </div>

        <div class="reprocesso-footer">
          <small class="timestamp">
            {{ reprocesso.so_reprocesso_created_at ? formatDateTime(new Date(reprocesso.so_reprocesso_created_at)) : '' }}
          </small>
        </div>
      </div>
    </div>
  </box>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOReprocessos } from '../../composables/useSOReprocessos';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import CameraCapture from '../../components/CameraCapture.vue';

type Id = number | string;

interface CompletedStep {
  so_work_order_step_id: Id;
  so_work_order_step_name?: string;
  so_work_order_step_production_step_id?: Id;
  [key: string]: unknown;
}

interface Reprocesso {
  so_reprocesso_id: Id;
  so_production_step_name?: string;
  so_reprocesso_status?: string;
  so_reprocesso_notes?: string;
  so_reprocesso_photo_before?: string;
  so_reprocesso_photo_after?: string;
  so_reprocesso_created_at?: string;
  [key: string]: unknown;
}

interface Props {
  orderItemId: number;
  completedSteps?: CompletedStep[];
  testIdPrefix?: string;
  mode?: 'display' | 'verify';
  editable?: boolean;
}

const { formatDateTime } = useDateFormatter();

const props = withDefaults(defineProps<Props>(), {
  completedSteps: () => [],
  testIdPrefix: 'so-reprocesso-section',
  mode: 'display',
  editable: true,
});

const { t } = useI18n();
const { reprocessos: rawReprocessos, listByOrderItem, uploadPhoto, completar } = useSOReprocessos();
const reprocessos = rawReprocessos as unknown as Ref<Reprocesso[]>;

const uploadingId = ref<Id | null>(null);

const canRequestReprocesso = computed<boolean>(() => {
  return !!props.completedSteps && props.completedSteps.length > 0;
});

const openReprocessoModal = (): void => {
  eventBus.emit('modal.soReprocesso.open', {
    orderItemId: props.orderItemId,
    completedSteps: props.completedSteps
  });
};

const handlePhotoAfterCaptured = async (reprocesso: Reprocesso, file: File): Promise<void> => {
  log('SOReprocessoSection', 'handlePhotoAfterCaptured', 'Uploading foto depois', {
    reprocessoId: reprocesso.so_reprocesso_id,
    size: file.size,
  });
  uploadingId.value = reprocesso.so_reprocesso_id;
  try {
    const photoUrl = await uploadPhoto(file) as string;
    await completar(reprocesso.so_reprocesso_id, { photo_after: photoUrl });
    eventBus.emit('globalMessage.success', t('Reprocesso finalizado'));
  } catch (err: unknown) {
    logError('SOReprocessoSection', 'handlePhotoAfterCaptured', 'Error', err);
    const message = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', t('Erro ao finalizar reprocesso') + ': ' + message);
  } finally {
    uploadingId.value = null;
  }
};

const refreshReprocessos = async (): Promise<void> => {
  try {
    await listByOrderItem(props.orderItemId);
  } catch (err: unknown) {
    // Error handled in composable
  }
};

onMounted(() => {
  refreshReprocessos();

  // Listen for events to refresh
  eventBus.on('reprocesso.created', refreshReprocessos);
  eventBus.on('reprocesso.completed', refreshReprocessos);
});
</script>

<style scoped>
.reprocessos-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.reprocessos-empty {
  padding: 12px;
  color: #999;
  font-style: italic;
  text-align: center;
}

.reprocesso-item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  background: #f9f9f9;
}

.reprocesso-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.step-name {
  font-weight: 600;
  color: #333;
}

.reprocesso-notes {
  margin: 8px 0;
  font-size: 0.9rem;
  color: #666;
}

.photos-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 12px 0;
}

.photo-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.photo-item label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
}

.preview-image {
  width: 100%;
  max-height: 150px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.placeholder-image {
  width: 100%;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 4px;
  border: 1px dashed #ddd;
  color: #999;
  font-size: 0.85rem;
  text-align: center;
  padding: 12px;
}

.placeholder-image.pending {
  background: #fffbf0;
  border-color: #ffd89b;
  color: #b3860b;
}

.uploading-hint {
  display: block;
  margin-top: 4px;
  color: var(--p-primary-color);
  font-style: italic;
  font-size: 0.75rem;
}

.reprocesso-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e0e0e0;
}

.timestamp {
  color: #999;
}
</style>
